/**
 * ISO 3166-1 alpha-2 country list with E.164 dial codes and localized names.
 *
 * Used by CountryCodePicker for the phone input. El Salvador is treated as
 * the home country (default selection); the rest are sorted alphabetically
 * by the active locale's name at render time.
 *
 * Source: ITU-T E.164 dial code allocation. List is conservative — one entry
 * per ISO 3166-1 alpha-2 code; territories with shared dial codes (e.g.
 * Puerto Rico shares +1 with the US) get their own row so the flag matches
 * the user's actual country.
 */

export interface Country {
  /** ISO 3166-1 alpha-2 (2-letter code). Drives the flag emoji + fallback text. */
  iso2: string
  /** ITU-T E.164 dial code WITHOUT the leading '+'. */
  dialCode: string
  /** Spanish name. */
  nameEs: string
  /** English name. */
  nameEn: string
}

/** ISO2 of the home country. */
export const DEFAULT_COUNTRY_ISO2 = 'SV'
/**
 * Countries whose numbers may be verified.
 *
 * This was `['SV']`, which did not merely prefer Salvadoran numbers — with one
 * entry the picker does not open, so anyone else literally cannot type theirs.
 * A Salvadoran in Los Angeles planning a trip home, and the App Store reviewer
 * who has to reach a working account, were both refused before they could ask.
 * Phone verification stays mandatory to have an account; being able to complete
 * it is what changes.
 *
 * Kept deliberately short. Every country added here is one more origin an SMS
 * pumping attack can bill us from, which is what pinning to +503 was really
 * protecting. Widen it when there is a reason, not by default — and remember
 * the OTP provider's geo-permissions must allow the same list or delivery fails
 * at the provider with the app none the wiser.
 */
export const SUPPORTED_PHONE_COUNTRY_ISO2S = [
  // Central America
  'BZ',
  'CR',
  'GT',
  'HN',
  'NI',
  'PA',
  'SV',
  // North America. CA shares +1 with US and providers enable the two together —
  // the NANP cannot separate them, so offering one and refusing the other would
  // accept a number the provider then delivers anyway.
  'CA',
  'MX',
  'US',
] as const

const COUNTRIES: readonly Country[] = [
  { iso2: 'AF', dialCode: '93', nameEs: 'Afganistán', nameEn: 'Afghanistan' },
  { iso2: 'AL', dialCode: '355', nameEs: 'Albania', nameEn: 'Albania' },
  { iso2: 'DZ', dialCode: '213', nameEs: 'Argelia', nameEn: 'Algeria' },
  { iso2: 'AS', dialCode: '1684', nameEs: 'Samoa Americana', nameEn: 'American Samoa' },
  { iso2: 'AD', dialCode: '376', nameEs: 'Andorra', nameEn: 'Andorra' },
  { iso2: 'AO', dialCode: '244', nameEs: 'Angola', nameEn: 'Angola' },
  { iso2: 'AI', dialCode: '1264', nameEs: 'Anguila', nameEn: 'Anguilla' },
  { iso2: 'AG', dialCode: '1268', nameEs: 'Antigua y Barbuda', nameEn: 'Antigua and Barbuda' },
  { iso2: 'AR', dialCode: '54', nameEs: 'Argentina', nameEn: 'Argentina' },
  { iso2: 'AM', dialCode: '374', nameEs: 'Armenia', nameEn: 'Armenia' },
  { iso2: 'AW', dialCode: '297', nameEs: 'Aruba', nameEn: 'Aruba' },
  { iso2: 'AU', dialCode: '61', nameEs: 'Australia', nameEn: 'Australia' },
  { iso2: 'AT', dialCode: '43', nameEs: 'Austria', nameEn: 'Austria' },
  { iso2: 'AZ', dialCode: '994', nameEs: 'Azerbaiyán', nameEn: 'Azerbaijan' },
  { iso2: 'BS', dialCode: '1242', nameEs: 'Bahamas', nameEn: 'Bahamas' },
  { iso2: 'BH', dialCode: '973', nameEs: 'Baréin', nameEn: 'Bahrain' },
  { iso2: 'BD', dialCode: '880', nameEs: 'Bangladés', nameEn: 'Bangladesh' },
  { iso2: 'BB', dialCode: '1246', nameEs: 'Barbados', nameEn: 'Barbados' },
  { iso2: 'BY', dialCode: '375', nameEs: 'Bielorrusia', nameEn: 'Belarus' },
  { iso2: 'BE', dialCode: '32', nameEs: 'Bélgica', nameEn: 'Belgium' },
  { iso2: 'BZ', dialCode: '501', nameEs: 'Belice', nameEn: 'Belize' },
  { iso2: 'BJ', dialCode: '229', nameEs: 'Benín', nameEn: 'Benin' },
  { iso2: 'BM', dialCode: '1441', nameEs: 'Bermudas', nameEn: 'Bermuda' },
  { iso2: 'BT', dialCode: '975', nameEs: 'Bután', nameEn: 'Bhutan' },
  { iso2: 'BO', dialCode: '591', nameEs: 'Bolivia', nameEn: 'Bolivia' },
  { iso2: 'BA', dialCode: '387', nameEs: 'Bosnia y Herzegovina', nameEn: 'Bosnia and Herzegovina' },
  { iso2: 'BW', dialCode: '267', nameEs: 'Botsuana', nameEn: 'Botswana' },
  { iso2: 'BR', dialCode: '55', nameEs: 'Brasil', nameEn: 'Brazil' },
  {
    iso2: 'IO',
    dialCode: '246',
    nameEs: 'Territorio Británico del Océano Índico',
    nameEn: 'British Indian Ocean Territory',
  },
  {
    iso2: 'VG',
    dialCode: '1284',
    nameEs: 'Islas Vírgenes Británicas',
    nameEn: 'British Virgin Islands',
  },
  { iso2: 'BN', dialCode: '673', nameEs: 'Brunéi', nameEn: 'Brunei' },
  { iso2: 'BG', dialCode: '359', nameEs: 'Bulgaria', nameEn: 'Bulgaria' },
  { iso2: 'BF', dialCode: '226', nameEs: 'Burkina Faso', nameEn: 'Burkina Faso' },
  { iso2: 'BI', dialCode: '257', nameEs: 'Burundi', nameEn: 'Burundi' },
  { iso2: 'KH', dialCode: '855', nameEs: 'Camboya', nameEn: 'Cambodia' },
  { iso2: 'CM', dialCode: '237', nameEs: 'Camerún', nameEn: 'Cameroon' },
  { iso2: 'CA', dialCode: '1', nameEs: 'Canadá', nameEn: 'Canada' },
  { iso2: 'CV', dialCode: '238', nameEs: 'Cabo Verde', nameEn: 'Cape Verde' },
  { iso2: 'KY', dialCode: '1345', nameEs: 'Islas Caimán', nameEn: 'Cayman Islands' },
  {
    iso2: 'CF',
    dialCode: '236',
    nameEs: 'República Centroafricana',
    nameEn: 'Central African Republic',
  },
  { iso2: 'TD', dialCode: '235', nameEs: 'Chad', nameEn: 'Chad' },
  { iso2: 'CL', dialCode: '56', nameEs: 'Chile', nameEn: 'Chile' },
  { iso2: 'CN', dialCode: '86', nameEs: 'China', nameEn: 'China' },
  { iso2: 'CX', dialCode: '61', nameEs: 'Isla de Navidad', nameEn: 'Christmas Island' },
  { iso2: 'CC', dialCode: '61', nameEs: 'Islas Cocos', nameEn: 'Cocos Islands' },
  { iso2: 'CO', dialCode: '57', nameEs: 'Colombia', nameEn: 'Colombia' },
  { iso2: 'KM', dialCode: '269', nameEs: 'Comoras', nameEn: 'Comoros' },
  { iso2: 'CK', dialCode: '682', nameEs: 'Islas Cook', nameEn: 'Cook Islands' },
  { iso2: 'CR', dialCode: '506', nameEs: 'Costa Rica', nameEn: 'Costa Rica' },
  { iso2: 'HR', dialCode: '385', nameEs: 'Croacia', nameEn: 'Croatia' },
  { iso2: 'CU', dialCode: '53', nameEs: 'Cuba', nameEn: 'Cuba' },
  { iso2: 'CW', dialCode: '599', nameEs: 'Curazao', nameEn: 'Curaçao' },
  { iso2: 'CY', dialCode: '357', nameEs: 'Chipre', nameEn: 'Cyprus' },
  { iso2: 'CZ', dialCode: '420', nameEs: 'República Checa', nameEn: 'Czech Republic' },
  {
    iso2: 'CD',
    dialCode: '243',
    nameEs: 'República Democrática del Congo',
    nameEn: 'Democratic Republic of the Congo',
  },
  { iso2: 'DK', dialCode: '45', nameEs: 'Dinamarca', nameEn: 'Denmark' },
  { iso2: 'DJ', dialCode: '253', nameEs: 'Yibuti', nameEn: 'Djibouti' },
  { iso2: 'DM', dialCode: '1767', nameEs: 'Dominica', nameEn: 'Dominica' },
  { iso2: 'DO', dialCode: '1', nameEs: 'República Dominicana', nameEn: 'Dominican Republic' },
  { iso2: 'EC', dialCode: '593', nameEs: 'Ecuador', nameEn: 'Ecuador' },
  { iso2: 'EG', dialCode: '20', nameEs: 'Egipto', nameEn: 'Egypt' },
  { iso2: 'SV', dialCode: '503', nameEs: 'El Salvador', nameEn: 'El Salvador' },
  { iso2: 'GQ', dialCode: '240', nameEs: 'Guinea Ecuatorial', nameEn: 'Equatorial Guinea' },
  { iso2: 'ER', dialCode: '291', nameEs: 'Eritrea', nameEn: 'Eritrea' },
  { iso2: 'EE', dialCode: '372', nameEs: 'Estonia', nameEn: 'Estonia' },
  { iso2: 'ET', dialCode: '251', nameEs: 'Etiopía', nameEn: 'Ethiopia' },
  { iso2: 'FK', dialCode: '500', nameEs: 'Islas Malvinas', nameEn: 'Falkland Islands' },
  { iso2: 'FO', dialCode: '298', nameEs: 'Islas Feroe', nameEn: 'Faroe Islands' },
  { iso2: 'FJ', dialCode: '679', nameEs: 'Fiyi', nameEn: 'Fiji' },
  { iso2: 'FI', dialCode: '358', nameEs: 'Finlandia', nameEn: 'Finland' },
  { iso2: 'FR', dialCode: '33', nameEs: 'Francia', nameEn: 'France' },
  { iso2: 'PF', dialCode: '689', nameEs: 'Polinesia Francesa', nameEn: 'French Polynesia' },
  { iso2: 'GA', dialCode: '241', nameEs: 'Gabón', nameEn: 'Gabon' },
  { iso2: 'GM', dialCode: '220', nameEs: 'Gambia', nameEn: 'Gambia' },
  { iso2: 'GE', dialCode: '995', nameEs: 'Georgia', nameEn: 'Georgia' },
  { iso2: 'DE', dialCode: '49', nameEs: 'Alemania', nameEn: 'Germany' },
  { iso2: 'GH', dialCode: '233', nameEs: 'Ghana', nameEn: 'Ghana' },
  { iso2: 'GI', dialCode: '350', nameEs: 'Gibraltar', nameEn: 'Gibraltar' },
  { iso2: 'GR', dialCode: '30', nameEs: 'Grecia', nameEn: 'Greece' },
  { iso2: 'GL', dialCode: '299', nameEs: 'Groenlandia', nameEn: 'Greenland' },
  { iso2: 'GD', dialCode: '1473', nameEs: 'Granada', nameEn: 'Grenada' },
  { iso2: 'GU', dialCode: '1671', nameEs: 'Guam', nameEn: 'Guam' },
  { iso2: 'GT', dialCode: '502', nameEs: 'Guatemala', nameEn: 'Guatemala' },
  { iso2: 'GG', dialCode: '44', nameEs: 'Guernsey', nameEn: 'Guernsey' },
  { iso2: 'GN', dialCode: '224', nameEs: 'Guinea', nameEn: 'Guinea' },
  { iso2: 'GW', dialCode: '245', nameEs: 'Guinea-Bisáu', nameEn: 'Guinea-Bissau' },
  { iso2: 'GY', dialCode: '592', nameEs: 'Guyana', nameEn: 'Guyana' },
  { iso2: 'HT', dialCode: '509', nameEs: 'Haití', nameEn: 'Haiti' },
  { iso2: 'HN', dialCode: '504', nameEs: 'Honduras', nameEn: 'Honduras' },
  { iso2: 'HK', dialCode: '852', nameEs: 'Hong Kong', nameEn: 'Hong Kong' },
  { iso2: 'HU', dialCode: '36', nameEs: 'Hungría', nameEn: 'Hungary' },
  { iso2: 'IS', dialCode: '354', nameEs: 'Islandia', nameEn: 'Iceland' },
  { iso2: 'IN', dialCode: '91', nameEs: 'India', nameEn: 'India' },
  { iso2: 'ID', dialCode: '62', nameEs: 'Indonesia', nameEn: 'Indonesia' },
  { iso2: 'IR', dialCode: '98', nameEs: 'Irán', nameEn: 'Iran' },
  { iso2: 'IQ', dialCode: '964', nameEs: 'Irak', nameEn: 'Iraq' },
  { iso2: 'IE', dialCode: '353', nameEs: 'Irlanda', nameEn: 'Ireland' },
  { iso2: 'IM', dialCode: '44', nameEs: 'Isla de Man', nameEn: 'Isle of Man' },
  { iso2: 'IL', dialCode: '972', nameEs: 'Israel', nameEn: 'Israel' },
  { iso2: 'IT', dialCode: '39', nameEs: 'Italia', nameEn: 'Italy' },
  { iso2: 'CI', dialCode: '225', nameEs: 'Costa de Marfil', nameEn: 'Ivory Coast' },
  { iso2: 'JM', dialCode: '1876', nameEs: 'Jamaica', nameEn: 'Jamaica' },
  { iso2: 'JP', dialCode: '81', nameEs: 'Japón', nameEn: 'Japan' },
  { iso2: 'JE', dialCode: '44', nameEs: 'Jersey', nameEn: 'Jersey' },
  { iso2: 'JO', dialCode: '962', nameEs: 'Jordania', nameEn: 'Jordan' },
  { iso2: 'KZ', dialCode: '7', nameEs: 'Kazajistán', nameEn: 'Kazakhstan' },
  { iso2: 'KE', dialCode: '254', nameEs: 'Kenia', nameEn: 'Kenya' },
  { iso2: 'KI', dialCode: '686', nameEs: 'Kiribati', nameEn: 'Kiribati' },
  { iso2: 'XK', dialCode: '383', nameEs: 'Kosovo', nameEn: 'Kosovo' },
  { iso2: 'KW', dialCode: '965', nameEs: 'Kuwait', nameEn: 'Kuwait' },
  { iso2: 'KG', dialCode: '996', nameEs: 'Kirguistán', nameEn: 'Kyrgyzstan' },
  { iso2: 'LA', dialCode: '856', nameEs: 'Laos', nameEn: 'Laos' },
  { iso2: 'LV', dialCode: '371', nameEs: 'Letonia', nameEn: 'Latvia' },
  { iso2: 'LB', dialCode: '961', nameEs: 'Líbano', nameEn: 'Lebanon' },
  { iso2: 'LS', dialCode: '266', nameEs: 'Lesoto', nameEn: 'Lesotho' },
  { iso2: 'LR', dialCode: '231', nameEs: 'Liberia', nameEn: 'Liberia' },
  { iso2: 'LY', dialCode: '218', nameEs: 'Libia', nameEn: 'Libya' },
  { iso2: 'LI', dialCode: '423', nameEs: 'Liechtenstein', nameEn: 'Liechtenstein' },
  { iso2: 'LT', dialCode: '370', nameEs: 'Lituania', nameEn: 'Lithuania' },
  { iso2: 'LU', dialCode: '352', nameEs: 'Luxemburgo', nameEn: 'Luxembourg' },
  { iso2: 'MO', dialCode: '853', nameEs: 'Macao', nameEn: 'Macau' },
  { iso2: 'MK', dialCode: '389', nameEs: 'Macedonia del Norte', nameEn: 'North Macedonia' },
  { iso2: 'MG', dialCode: '261', nameEs: 'Madagascar', nameEn: 'Madagascar' },
  { iso2: 'MW', dialCode: '265', nameEs: 'Malaui', nameEn: 'Malawi' },
  { iso2: 'MY', dialCode: '60', nameEs: 'Malasia', nameEn: 'Malaysia' },
  { iso2: 'MV', dialCode: '960', nameEs: 'Maldivas', nameEn: 'Maldives' },
  { iso2: 'ML', dialCode: '223', nameEs: 'Mali', nameEn: 'Mali' },
  { iso2: 'MT', dialCode: '356', nameEs: 'Malta', nameEn: 'Malta' },
  { iso2: 'MH', dialCode: '692', nameEs: 'Islas Marshall', nameEn: 'Marshall Islands' },
  { iso2: 'MR', dialCode: '222', nameEs: 'Mauritania', nameEn: 'Mauritania' },
  { iso2: 'MU', dialCode: '230', nameEs: 'Mauricio', nameEn: 'Mauritius' },
  { iso2: 'YT', dialCode: '262', nameEs: 'Mayotte', nameEn: 'Mayotte' },
  { iso2: 'MX', dialCode: '52', nameEs: 'México', nameEn: 'Mexico' },
  { iso2: 'FM', dialCode: '691', nameEs: 'Micronesia', nameEn: 'Micronesia' },
  { iso2: 'MD', dialCode: '373', nameEs: 'Moldavia', nameEn: 'Moldova' },
  { iso2: 'MC', dialCode: '377', nameEs: 'Mónaco', nameEn: 'Monaco' },
  { iso2: 'MN', dialCode: '976', nameEs: 'Mongolia', nameEn: 'Mongolia' },
  { iso2: 'ME', dialCode: '382', nameEs: 'Montenegro', nameEn: 'Montenegro' },
  { iso2: 'MS', dialCode: '1664', nameEs: 'Montserrat', nameEn: 'Montserrat' },
  { iso2: 'MA', dialCode: '212', nameEs: 'Marruecos', nameEn: 'Morocco' },
  { iso2: 'MZ', dialCode: '258', nameEs: 'Mozambique', nameEn: 'Mozambique' },
  { iso2: 'MM', dialCode: '95', nameEs: 'Birmania', nameEn: 'Myanmar' },
  { iso2: 'NA', dialCode: '264', nameEs: 'Namibia', nameEn: 'Namibia' },
  { iso2: 'NR', dialCode: '674', nameEs: 'Nauru', nameEn: 'Nauru' },
  { iso2: 'NP', dialCode: '977', nameEs: 'Nepal', nameEn: 'Nepal' },
  { iso2: 'NL', dialCode: '31', nameEs: 'Países Bajos', nameEn: 'Netherlands' },
  { iso2: 'NC', dialCode: '687', nameEs: 'Nueva Caledonia', nameEn: 'New Caledonia' },
  { iso2: 'NZ', dialCode: '64', nameEs: 'Nueva Zelanda', nameEn: 'New Zealand' },
  { iso2: 'NI', dialCode: '505', nameEs: 'Nicaragua', nameEn: 'Nicaragua' },
  { iso2: 'NE', dialCode: '227', nameEs: 'Níger', nameEn: 'Niger' },
  { iso2: 'NG', dialCode: '234', nameEs: 'Nigeria', nameEn: 'Nigeria' },
  { iso2: 'NU', dialCode: '683', nameEs: 'Niue', nameEn: 'Niue' },
  { iso2: 'KP', dialCode: '850', nameEs: 'Corea del Norte', nameEn: 'North Korea' },
  { iso2: 'NO', dialCode: '47', nameEs: 'Noruega', nameEn: 'Norway' },
  { iso2: 'OM', dialCode: '968', nameEs: 'Omán', nameEn: 'Oman' },
  { iso2: 'PK', dialCode: '92', nameEs: 'Pakistán', nameEn: 'Pakistan' },
  { iso2: 'PW', dialCode: '680', nameEs: 'Palaos', nameEn: 'Palau' },
  { iso2: 'PS', dialCode: '970', nameEs: 'Palestina', nameEn: 'Palestine' },
  { iso2: 'PA', dialCode: '507', nameEs: 'Panamá', nameEn: 'Panama' },
  { iso2: 'PG', dialCode: '675', nameEs: 'Papúa Nueva Guinea', nameEn: 'Papua New Guinea' },
  { iso2: 'PY', dialCode: '595', nameEs: 'Paraguay', nameEn: 'Paraguay' },
  { iso2: 'PE', dialCode: '51', nameEs: 'Perú', nameEn: 'Peru' },
  { iso2: 'PH', dialCode: '63', nameEs: 'Filipinas', nameEn: 'Philippines' },
  { iso2: 'PL', dialCode: '48', nameEs: 'Polonia', nameEn: 'Poland' },
  { iso2: 'PT', dialCode: '351', nameEs: 'Portugal', nameEn: 'Portugal' },
  { iso2: 'PR', dialCode: '1', nameEs: 'Puerto Rico', nameEn: 'Puerto Rico' },
  { iso2: 'QA', dialCode: '974', nameEs: 'Catar', nameEn: 'Qatar' },
  { iso2: 'CG', dialCode: '242', nameEs: 'República del Congo', nameEn: 'Republic of the Congo' },
  { iso2: 'RO', dialCode: '40', nameEs: 'Rumanía', nameEn: 'Romania' },
  { iso2: 'RU', dialCode: '7', nameEs: 'Rusia', nameEn: 'Russia' },
  { iso2: 'RW', dialCode: '250', nameEs: 'Ruanda', nameEn: 'Rwanda' },
  { iso2: 'BL', dialCode: '590', nameEs: 'San Bartolomé', nameEn: 'Saint Barthélemy' },
  {
    iso2: 'KN',
    dialCode: '1869',
    nameEs: 'San Cristóbal y Nieves',
    nameEn: 'Saint Kitts and Nevis',
  },
  { iso2: 'LC', dialCode: '1758', nameEs: 'Santa Lucía', nameEn: 'Saint Lucia' },
  { iso2: 'MF', dialCode: '590', nameEs: 'San Martín', nameEn: 'Saint Martin' },
  {
    iso2: 'VC',
    dialCode: '1784',
    nameEs: 'San Vicente y las Granadinas',
    nameEn: 'Saint Vincent and the Grenadines',
  },
  { iso2: 'WS', dialCode: '685', nameEs: 'Samoa', nameEn: 'Samoa' },
  { iso2: 'SM', dialCode: '378', nameEs: 'San Marino', nameEn: 'San Marino' },
  { iso2: 'ST', dialCode: '239', nameEs: 'Santo Tomé y Príncipe', nameEn: 'São Tomé and Príncipe' },
  { iso2: 'SA', dialCode: '966', nameEs: 'Arabia Saudita', nameEn: 'Saudi Arabia' },
  { iso2: 'SN', dialCode: '221', nameEs: 'Senegal', nameEn: 'Senegal' },
  { iso2: 'RS', dialCode: '381', nameEs: 'Serbia', nameEn: 'Serbia' },
  { iso2: 'SC', dialCode: '248', nameEs: 'Seychelles', nameEn: 'Seychelles' },
  { iso2: 'SL', dialCode: '232', nameEs: 'Sierra Leona', nameEn: 'Sierra Leone' },
  { iso2: 'SG', dialCode: '65', nameEs: 'Singapur', nameEn: 'Singapore' },
  { iso2: 'SX', dialCode: '1721', nameEs: 'Sint Maarten', nameEn: 'Sint Maarten' },
  { iso2: 'SK', dialCode: '421', nameEs: 'Eslovaquia', nameEn: 'Slovakia' },
  { iso2: 'SI', dialCode: '386', nameEs: 'Eslovenia', nameEn: 'Slovenia' },
  { iso2: 'SB', dialCode: '677', nameEs: 'Islas Salomón', nameEn: 'Solomon Islands' },
  { iso2: 'SO', dialCode: '252', nameEs: 'Somalia', nameEn: 'Somalia' },
  { iso2: 'ZA', dialCode: '27', nameEs: 'Sudáfrica', nameEn: 'South Africa' },
  { iso2: 'KR', dialCode: '82', nameEs: 'Corea del Sur', nameEn: 'South Korea' },
  { iso2: 'SS', dialCode: '211', nameEs: 'Sudán del Sur', nameEn: 'South Sudan' },
  { iso2: 'ES', dialCode: '34', nameEs: 'España', nameEn: 'Spain' },
  { iso2: 'LK', dialCode: '94', nameEs: 'Sri Lanka', nameEn: 'Sri Lanka' },
  { iso2: 'SD', dialCode: '249', nameEs: 'Sudán', nameEn: 'Sudan' },
  { iso2: 'SR', dialCode: '597', nameEs: 'Surinam', nameEn: 'Suriname' },
  { iso2: 'SZ', dialCode: '268', nameEs: 'Esuatini', nameEn: 'Eswatini' },
  { iso2: 'SE', dialCode: '46', nameEs: 'Suecia', nameEn: 'Sweden' },
  { iso2: 'CH', dialCode: '41', nameEs: 'Suiza', nameEn: 'Switzerland' },
  { iso2: 'SY', dialCode: '963', nameEs: 'Siria', nameEn: 'Syria' },
  { iso2: 'TW', dialCode: '886', nameEs: 'Taiwán', nameEn: 'Taiwan' },
  { iso2: 'TJ', dialCode: '992', nameEs: 'Tayikistán', nameEn: 'Tajikistan' },
  { iso2: 'TZ', dialCode: '255', nameEs: 'Tanzania', nameEn: 'Tanzania' },
  { iso2: 'TH', dialCode: '66', nameEs: 'Tailandia', nameEn: 'Thailand' },
  { iso2: 'TL', dialCode: '670', nameEs: 'Timor Oriental', nameEn: 'Timor-Leste' },
  { iso2: 'TG', dialCode: '228', nameEs: 'Togo', nameEn: 'Togo' },
  { iso2: 'TK', dialCode: '690', nameEs: 'Tokelau', nameEn: 'Tokelau' },
  { iso2: 'TO', dialCode: '676', nameEs: 'Tonga', nameEn: 'Tonga' },
  { iso2: 'TT', dialCode: '1868', nameEs: 'Trinidad y Tobago', nameEn: 'Trinidad and Tobago' },
  { iso2: 'TN', dialCode: '216', nameEs: 'Túnez', nameEn: 'Tunisia' },
  { iso2: 'TR', dialCode: '90', nameEs: 'Turquía', nameEn: 'Turkey' },
  { iso2: 'TM', dialCode: '993', nameEs: 'Turkmenistán', nameEn: 'Turkmenistan' },
  {
    iso2: 'TC',
    dialCode: '1649',
    nameEs: 'Islas Turcas y Caicos',
    nameEn: 'Turks and Caicos Islands',
  },
  { iso2: 'TV', dialCode: '688', nameEs: 'Tuvalu', nameEn: 'Tuvalu' },
  { iso2: 'UG', dialCode: '256', nameEs: 'Uganda', nameEn: 'Uganda' },
  { iso2: 'UA', dialCode: '380', nameEs: 'Ucrania', nameEn: 'Ukraine' },
  { iso2: 'AE', dialCode: '971', nameEs: 'Emiratos Árabes Unidos', nameEn: 'United Arab Emirates' },
  { iso2: 'GB', dialCode: '44', nameEs: 'Reino Unido', nameEn: 'United Kingdom' },
  { iso2: 'US', dialCode: '1', nameEs: 'Estados Unidos', nameEn: 'United States' },
  { iso2: 'UY', dialCode: '598', nameEs: 'Uruguay', nameEn: 'Uruguay' },
  { iso2: 'UZ', dialCode: '998', nameEs: 'Uzbekistán', nameEn: 'Uzbekistan' },
  { iso2: 'VU', dialCode: '678', nameEs: 'Vanuatu', nameEn: 'Vanuatu' },
  { iso2: 'VA', dialCode: '39', nameEs: 'Ciudad del Vaticano', nameEn: 'Vatican City' },
  { iso2: 'VE', dialCode: '58', nameEs: 'Venezuela', nameEn: 'Venezuela' },
  { iso2: 'VN', dialCode: '84', nameEs: 'Vietnam', nameEn: 'Vietnam' },
  { iso2: 'WF', dialCode: '681', nameEs: 'Wallis y Futuna', nameEn: 'Wallis and Futuna' },
  { iso2: 'YE', dialCode: '967', nameEs: 'Yemen', nameEn: 'Yemen' },
  { iso2: 'ZM', dialCode: '260', nameEs: 'Zambia', nameEn: 'Zambia' },
  { iso2: 'ZW', dialCode: '263', nameEs: 'Zimbabue', nameEn: 'Zimbabwe' },
]

const BY_ISO2: Record<string, Country> = Object.fromEntries(COUNTRIES.map((c) => [c.iso2, c]))

export function findCountry(iso2: string): Country | undefined {
  return BY_ISO2[iso2.toUpperCase()]
}

export function supportedPhoneCountries(
  iso2s: readonly string[] = SUPPORTED_PHONE_COUNTRY_ISO2S,
): readonly Country[] {
  const allowed = new Set(iso2s.map((iso2) => iso2.toUpperCase()))
  const selected = COUNTRIES.filter((country) => allowed.has(country.iso2))
  return selected.length ? selected : [findCountry(DEFAULT_COUNTRY_ISO2)!]
}
